import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useNavigate, useParams, Link } from "../navigation";
import { Trans } from "react-i18next"
import _ from 'lodash'

import { useProducts } from "./ProductsProvider";
import Modal from '../components/Modal';
import { Formik, Form, Field, useField } from 'formik';
import { DatePicker } from '../components/input/DatePicker';
import { useMessages } from '../Messages';
import { countryCodes } from "../data";
import { ToggleButtonField } from '../components/ToggleButton';
import { saveCrudLinks } from '../BackendProvider';
import { useRoles } from '../user/Auth';
import { TagListField } from '../components/TagList';
import { Commodity, commodityUrl, useProductMapping } from './products';
import { BreadCrumbs } from "../components/BreadCrumbs";
import Image from "../components/Image";
import { IconEdit } from "../components/icons";
import DeleteButton from "../components/DeleteButton";
import clsx from "clsx";
import { familyUrl } from "../patents/utils";
import { useTrademarks } from "../trademarks/TrademarksProvider";
import { trademarkFamilyUrl } from "../trademarks/utils";
import { useExternal } from "../external/";
import { competitorFamilyUrl } from "../external/utils";
import ProtectionIcon from "../components/ProtectionIcon";
import { FocusedBrowser } from "../documents/FocusedBrowser";
import { LinkedTabCard } from "../components/TabCard";
import { createChangedProductState } from "../components/update";
import { ProductChanged } from "../components/update/ProductChanged";
import { isEqualTag, useTags } from "../tags/TagsProvider";
import IncomeProductView from "../income/IncomeProductView";
import { IncomeProvider } from "../income/IncomeProvider";
import { useAgents } from "../agents/AgentsProvider";
import { AgentsEdit } from "../agents/AgentList";
import { nameOf } from "../agents/utils";
import { ProductContext, useProduct } from ".";
import ProductTasks from "./ProductTasks";
import ProductComments from "./ProductComments";

export function ProductIndex() {
  const { t } = useTranslation();
  const { hasDocuments, hasRevenues } = useRoles()

  const { id: _id } = useParams();
  const id = parseInt(_id);

  const { commodityById } = useProducts()

  const commodity = commodityById[id]

  if (!commodity) return null
  // TODO: navigate to portfolio if not found for too long...

  return <>
    {/* @ts-ignore */}
    <Helmet>
      <title>{[commodity.commodityReference, commodity.commodityClass, t('products'), 'Patent Cockpit'].filter(Boolean).join(' | ')}</title>
    </Helmet>
    <BreadCrumbs parts={[
      { to: '/products/portfolio', label: t('products') },
      <ProductBreadCrumb key={commodity.commodityReference} {...{ commodity }} />,
    ]} />
    <div className="overflow-auto grow px-2 sm:px-4">
      <div className="w-fit lg:min-w-4xl xl:min-w-6xl" >
        <ProductProvider commodity={commodity}>
          <LinkedTabCard links={[
            { to: '.', label: t('overview') },
            hasDocuments && { to: 'documents', label: t('documents') },
            hasRevenues && { to: 'income', label: t('income') },
          ]} />
        </ProductProvider>
      </div>
    </div>
  </>
}

export function ProductDocuments() {
  const commodity = useProduct()
  return (
    <FocusedBrowser entity="commodity" internalReference={commodity.commodityReference} parent={commodity.commodityClass} parentName={commodity.commodityClass} />
  )
}

export function ProductIncome() {
  const commodity = useProduct()
  return <IncomeProvider>
    <IncomeProductView {...{ commodity }} />
  </IncomeProvider>
}

export default function ProductView() {
  const { t } = useTranslation();
  // TODO: add flag for product tags
  const { isEditUser, hasProductCountries, hasProductTags, isDev, hasProductOwners, hasTasks } = useRoles()
  const navigate = useNavigate()

  const { tagsLookup } = useTags()
  const { deleteCommodity, reload, commodityCountryLinksByCommodityId, commodityAgentsByCommodityId } = useProducts()
  const {agentById} = useAgents()

  const commodity = useProduct()

  if (!commodity) return null

  const commodityCountryLinks = commodityCountryLinksByCommodityId[commodity.commodityId] ?? []
  const countryTags = commodityCountryLinks.map((l) => l.countryCode);
  const tagsOfProduct = tagsLookup?.["commodity"]?.[commodity.commodityId] ?? []
  // console.log({tagsOfProduct, tagsLookup})

  const owners = (commodityAgentsByCommodityId[commodity.commodityId] ?? [])
    .map(l => {
      const agent = agentById[l.agentId]
      if (!agent) return null
      return { ...agent, name: nameOf(agent) }
    })
    .filter(Boolean)

  const ribbonStyle = clsx('ribbon', commodity.status === "active" && "bg-pcx-200")

  return <>
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(32rem,48rem)_400px] xl:grid-cols-[minmax(32rem,48rem)_400px] gap-2 divide-pcx-200 max-lg:divide-y-2 lg:divide-x-2">
      {/* Main content */}
      <div>
        <div className={clsx(
          "grid grid-cols-1 md:grid-cols-[2fr_3fr_2fr] gap-x-2",
          "max-w-screen-lg",
        )}>
          <div className="px-2 md:col-span-3 grid grid-cols-subgrid gap-3">
            <Image  {...{
              entity: "commodity",
              entityId: commodity.commodityId,
              isEditable: isEditUser,
            }} />

            <div>
              <p className="whitespace-pre-wrap">
                {commodity.commodityDescription ?? ''}
              </p>
              <div className="flex flex-row flex-wrap gap-1 text-sm text-gray-600">
                {hasProductTags &&
                  _(tagsOfProduct)
                    .uniq()
                    .sortBy((t) => t)
                    .map((t) => <div className="ribbon w-fit py-0.5" key={t}>{t}</div>)
                    .value()}
              </div>
            </div>

            <div className="flex flex-row gap-1 justify-between">
              <div className="flex items-start flex-wrap gap-1 text-sm">
                <div className={ribbonStyle}>{t(commodity.status)}</div>
                {commodity.isThirdParty && (
                  <div className={ribbonStyle}>{t("isThirdParty")}</div>
                )}
                {(hasProductCountries ? countryTags : []).map((cc) => (
                  <div key={cc} className={ribbonStyle}>
                    {cc}
                  </div>
                ))}
                {hasProductOwners && owners?.length > 0 &&
                  <div className="w-full">
                    <h5 className="text-gray-500 mb-1">{t('owner')}</h5>
                    <div className="flex flex-row flex-wrap text-sm">
                      {owners.map(l => <div key={l.agentId} className="after:content-[';_'] pr-1 last:pr-0 last:after:content-[''] ">{l.name}</div>)}
                    </div>
                  </div>
                }
                {(commodity.status === 'active' || commodity.status === 'stopped') && (
                  <div className="w-full grid grid-cols-[auto_auto] gap-x-4 gap-y-1 mt-2 text-sm">
                    <div className="text-gray-500">{t('status')}</div>
                    <div>{t(commodity.status)}</div>
                    {commodity.onMarketSince && <>
                      <div className="text-gray-500">{t('onMarketSince')}</div>
                      <div>{commodity.onMarketSince}</div>
                    </>}
                    <div className="text-gray-500">{t('isThirdParty')}</div>
                    <div>{commodity.isThirdParty ? t('yes') : t('no')}</div>
                  </div>
                )}
              </div>
              {isEditUser &&
                <div className="flex flex-row gap-1">
                  <Link
                    to="edit"
                    className="btn-primary h-5 w-5 p-px"
                    title={t("edit")}
                  >
                    <IconEdit className="" />
                  </Link>
                  <DeleteButton
                    className="btn-warn h-5 w-5 p-px"
                    deleteAction={() => deleteCommodity(commodity).then(() => {
                      reload();
                      navigate('/products/portfolio')
                    })}
                  />
                </div>}
            </div>
          </div>
          <div className="p-2 md:col-span-3 grid grid-cols-subgrid border-t-2 mt-2 border-pcx-200">
            <div>
              <ProductPatentMappingDetails {...commodity} />
            </div>
            <div>
              <ProductTrademarkMappingDetails {...commodity} />
            </div>
            <div>
              <ProductExternalMappingDetails {...commodity} />
            </div>
          </div>
        </div>
      </div>
      {/* Sidebar with Tasks and Comments */}
      <div className="space-y-5 max-lg:pt-2 lg:pl-2">
        {hasTasks && <ProductTasks commodityId={commodity.commodityId} />}
        <ProductComments commodityId={commodity.commodityId} name={commodity.commodityReference} />
      </div>
    </div>
    <ProductChanged />
    <Outlet />
  </>
}

function ProductBreadCrumb({commodity}: {commodity: Commodity}) {
  return (
    <Link to={commodityUrl(commodity)}>
      {commodity.commodityClass && <span className="hidden lg:inline mr-1 text-pcx-500">{commodity.commodityClass} /</span>}
      <span>{commodity.commodityReference}</span>  
    </Link>
  )
}

type PostCommodityProps = Commodity & {tags?: string[], countryCodes?: string[], commodityOwners: number[]}

export function PostProduct({ isAdding = false }) {
  const { t } = useTranslation();

  const { isEditUser, hasProductCountries, hasProductTags, hasProductOwners, hasDocuments } = useRoles();
  const { setErrorMessage } = useMessages();

  const {
    tags,
    tagsLookup,
    postTag, deleteTag,
  } = useTags();
  const {
    commodities, commodityByReference, commodityById, postCommodity, 
    commodityCountryLinks: _commodityCountryLinks, postCommodityCountryLink, deleteCommodityCountryLink,
    commodityAgentsByCommodityId, postCommodityAgent, deleteCommodityAgent,
  } = useProducts();
  //const {agentById} = useAgents()

  const { id: _id } = useParams();
  const id = parseInt(_id);
  const navigate = useNavigate();

  const commodityCountryLinks =
    !isAdding && hasProductCountries
      ? _commodityCountryLinks.filter((l) => l.commodityId === id)
      : [];

  const commodityOwners = (commodityAgentsByCommodityId[id] ?? []).map(l => l.agentId) //.map(l => agentById[l.agentId]).filter(Boolean)

  const countryTags = commodityCountryLinks.map((l) => l.countryCode);
  const tagsOfProduct = tagsLookup["commodity"]?.[id] ?? [];
  const allTags = _.uniq(tags.map((t) => t.tag));

  const commodity = commodities.find((c) => c.commodityId === id);

  const existingClasses = _.uniq(
    commodities
      .map((c) => c.commodityClass)
      .filter((c) => c !== "" && c !== undefined)
  );

  const initialValues: PostCommodityProps = {
    ...(isAdding
      ? {
        commodityId: undefined,
        commodityClass: undefined,
        commodityReference: "",
        commodityDescription: "",
        status: "active",
        isThirdParty: false,
        tags: [],
        countryCodes: [],
      }
      : commodity),
    commodityOwners,
    tags: tagsOfProduct,
  }

  async function handleSubmit(values: PostCommodityProps) {
      const withId = await postCommodity(values)
      const commodityId = withId.commodityId
      await saveCrudLinks(
        tagsOfProduct.map((tag) => asTag(tag, commodityId)),
        (values.tags ?? []).map((tag) => asTag(tag, commodityId)),
        postTag, deleteTag, isEqualTag)
      await saveCrudLinks(
        countryTags.map((cc) => asLink(cc, commodityId)),
        (values.countryCodes ?? []).map((cc) => asLink(cc, commodityId)),
        postCommodityCountryLink,
        deleteCommodityCountryLink,
      )
      // console.log('updating owners')
      await saveCrudLinks(
        commodityOwners.map((agentId) => ({agentId, commodityId})),
        (values.commodityOwners ?? []).map((agentId) => ({agentId, commodityId})),
        postCommodityAgent, deleteCommodityAgent,
      )
      // console.log('done, updating owners')
      return withId
  }

  return (
    <Modal overflowHidden={false}>
      <Formik
        initialValues={initialValues}
        onSubmit={async (values: PostCommodityProps) => {
          try {
            const newProduct = {...values, commodityReference: values.commodityReference.trim()}
            const result = await handleSubmit(newProduct)
            const state = createChangedProductState({ hasDocuments, oldProduct: isAdding ? undefined : commodity, newProduct })
            navigate(commodityUrl(result), {state})
            //navigate(commodityReferenceUrl({reference: result.commodityReference}), {state})
          } catch (err) {
            setErrorMessage(err.message)
          }
        }}
        enableReinitialize
        validate={(values) => {
          const errors = {};
          if (values.commodityReference in commodityByReference) {
            const c = commodityByReference[values.commodityReference]
            if (c.commodityId !== values.commodityId) {
              errors['commodityReference'] = c.status === 'stopped'
                ? t("is-already-used-what", { what: t("stopped-product") })
                : t("is-already-used");
            }
          }
          return errors;
        }}
      >{({isSubmitting}) =>
        <Form>
          <div className="p-4 grid grid-cols-2 gap-3">
            <h2 className="col-span-2">
              {isAdding ? (
                t("add-product")
              ) : (
                <Trans
                  i18nKey="edit-reference"
                  values={{ reference: commodity?.commodityReference }}
                />
              )}
            </h2>
            {/* TODO: add existing classes as proposal */}
            <RichField
              className="form-input"
              labelClassName="col-span-2 sm:col-span-1"
              name="commodityClass"
              //pattern="[a-zA-Z0-9 ._\-]*" title={t('use-usernames')}
              pattern="[\p{L}\p{N} ._\-]*" title={t('use-usernames')}
              proposals={existingClasses}
            />
            <RichField
              className="form-input"
              labelClassName="col-span-2 sm:col-span-1"
              name="commodityReference"
              required autoFocus
              pattern="[\p{L}\p{N} ._\-]*" title={t('use-usernames')}
            />
            <RichField
              className="form-input h-48 w-full"
              labelClassName="col-span-2"
              name="commodityDescription"
              as="textarea"
            />

            <RichField className="form-select" name="status" as="select">
              {["active", "stopped", "planned"].map((s) => (
                <option key={s} value={s}>
                  {t(s)}
                </option>
              ))}
            </RichField>

            <label>
              <div className="label mb-1">{t("onMarketSince")}</div>
              <Field className="h-8 px-1 form-input" name="onMarketSince" as={DatePicker} />
            </label>

            <label className="pl-0.5">
              <div className="label -ml-0.5 mb-1">{t("isThirdParty")}</div>
              <ToggleButtonField name="isThirdParty" />
            </label>

            {hasProductTags && (
              <label className="col-span-2 sm:col-span-1">
                <div className="label mb-1">{t("tags")}</div>
                <TagListField
                  name="tags"
                  availableTags={allTags} 
                  primary
                  allowNew
                />
              </label>
            )}
            {hasProductCountries && (
              <label className="col-span-2 sm:col-span-1">
                <div className="label mb-1">{t("countryCode")}</div>
                <TagListField
                  name="countryCodes"
                  placeholder={t("countryCode")}
                  availableTags={countryCodes}
                />
              </label>
            )}
            {hasProductOwners && 
              <div className="col-span-2">
                <label htmlFor="commodityOwners" className="label mb-1">{t('owner')}</label>
                <OwnersField name="commodityOwners" />
              </div>
            }
          </div>
          <div className="p-4 flex flex-row-reverse gap-4 bg-pcx-200 rounded-b-xl">
            {isEditUser && (
              <button id="save" className="btn-primary btn-loading" type="submit" disabled={isSubmitting}>
                {t("save")}
                {isSubmitting && <span className="btn-loading-indicator" />}
              </button>
            )}
            {/* @ts-ignore */}
            <Link className="btn-secondary" to={-1 }>
              {t("cancel")}
            </Link>
          </div>
        </Form>
      }</Formik>
    </Modal>
  );
}

function asTag(tag, entityId) {
  return { tag, entity: "commodity", entityId };
}

function OwnersField({name}) {
  const [field, meta, helpers] = useField<number []>({name})

  return <div className="flex flex-row flex-wrap gap-1 max-w-xl">
    <AgentsEdit {...{ name, value: field.value ?? [], onChange: (v) => helpers.setValue(v).then(() => { }) }} />
  </div>
}


function RichField({children = null, labelClassName = '', ...props }) {
    const {t} = useTranslation()
    // eslint-disable-next-line
    const [field, meta] = useField(props as {name: string})
    const { name } = props
    return (
        <label className={labelClassName}>
            <div className='label mb-1'>{t(name)}</div>
            <Field id={name} {...{...props, list: props?.proposals && `${name}s`}} >
                {children}
            </Field>
            {meta.touched && meta.error && <div className='text-red-500 mt-1 ml-1'>{meta.error}</div>}
            {props?.proposals &&
                <datalist id={`${name}s`}>
                    {_(props.proposals).sortBy(p => p.toLowerCase()).map(p => <option key={p} value={p} />).value()}
                </datalist>}
        </label>
    )
}

const asLink = (countryCode: string, commodityId: number) => ({ countryCode, commodityId });

function ProductPatentMappingDetails({commodityId, isThirdParty}: Commodity) {
    const {t} = useTranslation()
    const {familiesByCommodityId, claimScopeFamiliesByCommodityId} = useProductMapping()

  const families = _([
    ...familiesByCommodityId[commodityId] ?? [],
    ...claimScopeFamiliesByCommodityId[commodityId] ?? []
  ])
  .uniqBy(f => f?.patentFamilyId)
  .filter(Boolean)
  .value()

  return <>
    <div className="mb-1 flex flex-row items-center gap-8">
      <h4>
        {t('patents')}
      </h4>
      <Link to="patents" className="btn-secondary h-5 w-5 p-px"><IconEdit className="" /></Link>
    </div>
    <ul className="list-none">
      <li className="hidden last:block text-slate-500">{t("no-overlapping-patents")}</li>
      {_(families)
        .sortBy(f => f.internalReference)
        .map(family => <li key={family.patentFamilyId}>
          <Link className="underline-link flex flex-row gap-1 items-center" to={familyUrl(family)}>
            <ProtectionIcon {...{ isThirdParty }} /> {family.internalReference}
          </Link>
        </li>)
        .value()}
    </ul>
  </>
}

function ProductTrademarkMappingDetails({commodityId, isThirdParty}: Commodity) {
    const {t} = useTranslation()
    const {hasBrands} = useRoles()
    const {trademarkProductMappingsByCommodityId, trademarkFamilyById} = useTrademarks()

    const families = (trademarkProductMappingsByCommodityId[commodityId] ?? [])
        .map(m => ({...m, ...trademarkFamilyById[m.familyId]}))
        .filter(m => m.protected)

    if (!hasBrands) return null

    return <>
        <div className="mb-1 max-sm:mt-4 flex flex-row items-center gap-8">
            <h4>
                {t('trademarks')}
            </h4>
            <Link to="trademarks" className="btn-secondary h-5 w-5 p-px"><IconEdit className="" /></Link>
        </div>
        <ul className="list-none">
            <li className="hidden last:block text-slate-500">{t("no-overlapping-trademarks")}</li>
            {_(families)
                .sortBy(f => f.reference)
                .map(family => <li key={family.familyId}>
                    <Link className="underline-link flex flex-row gap-1 items-center" to={trademarkFamilyUrl(family)}>
                        <ProtectionIcon {...{ isThirdParty }} /> {family.reference}
                    </Link>
                </li>)
                .value()}
        </ul>
    </>
}

function ProductExternalMappingDetails({commodityId, isThirdParty}: Commodity) {
    const {t} = useTranslation()
    const {hasExternal} = useRoles()
    const {competitorCommodityLinks, familyById} = useExternal()

    const families = (competitorCommodityLinks ?? [])
        .filter(l => l.commodityId === commodityId && l.protected)
        .map(l => familyById[l.competitorFamilyId])
        .filter(Boolean)

    if (!hasExternal) return null

    return <>
        <div className="mb-1 max-sm:mt-4 flex flex-row items-start gap-2">
            <h4>{t('external')}</h4>
            <Link to="external" className="btn-secondary h-5 w-5 p-px shrink-0 mt-0.5"><IconEdit className="" /></Link>
        </div>
        <ul className="list-none">
            <li className="hidden last:block text-slate-500">{t("no-overlapping-external")}</li>
            {_(families)
                .sortBy(f => f.internalReference)
                .map(family => <li key={family.competitorFamilyId}>
                    <Link className="underline-link flex flex-row gap-1 items-center"
                        to={competitorFamilyUrl(family)}>
                        <ProtectionIcon {...{ isThirdParty }} /> {family.internalReference}
                    </Link>
                </li>)
                .value()}
        </ul>
    </>
}

export function ProductByRef() {
  const { commodityByReference } = useProducts();
  const { reference } = useParams();

  const commodity = commodityByReference[reference];

  if (!commodity) return null;

  return <Navigate to={commodityUrl(commodity)} />;
}

export function ProductProvider({ children, commodity }: { children: React.ReactNode, commodity: Commodity }) {
  return (
    <ProductContext.Provider value={commodity}>
      {children}
    </ProductContext.Provider>
  )
}
